import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PrintIcon from "@mui/icons-material/Print";
import ReactMarkdown from "react-markdown";

const MarkdownViewer = ({ open, onClose, docs, selectedDoc, onDocSelect }) => {
  const theme = useTheme();

  const displayDoc = selectedDoc || docs[0];
  const showDocList = docs.length > 1;

  const handlePrint = () => {
    const printWindow = window.open("", "", "width=800,height=600");
    printWindow.document.write(`
      <html>
        <head>
          <title>${displayDoc.title}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1, h2, h3 { color: #333; }
            pre { background: #f5f5f5; padding: 10px; border-radius: 4px; overflow-x: auto; }
            code { background: #f5f5f5; padding: 2px 4px; border-radius: 2px; }
            blockquote { border-left: 4px solid #ccc; padding-left: 10px; margin-left: 0; color: #666; }
          </style>
        </head>
        <body>
          <h1>${displayDoc.title}</h1>
          <p><em>${displayDoc.description || ""}</em></p>
          <hr />
          <div>${document.querySelector(".markdown-content").innerHTML}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: "95vh",
          maxHeight: "95vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">
          {displayDoc?.title || "Documentation"}
        </Typography>
        {displayDoc && (
          <Button
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            size="small"
          >
            Print
          </Button>
        )}
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ display: "flex", flex: 1, p: 2 }}
      >
        <Box sx={{ display: "flex", gap: 2, height: "100%", width: "100%" }}>
          {/* Doc List (if multiple) */}
          {showDocList && (
            <Box
              sx={{
                minWidth: 250,
                borderRight: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: "bold" }}
              >
                Available Documents
              </Typography>
              <List sx={{ flex: 1, overflow: "auto" }}>
                {docs.map((doc, idx) => (
                  <ListItem
                    key={idx}
                    disablePadding
                  >
                    <ListItemButton
                      selected={selectedDoc?.key === doc.key}
                      onClick={() => onDocSelect(doc)}
                    >
                      <ListItemText
                        primary={doc.title}
                        secondary={
                          <>
                            <Typography
                              variant="caption"
                              display="block"
                            >
                              {doc.readingTimeMinutes} min read
                            </Typography>
                            <Chip
                              label={doc.type}
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          </>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Main Content */}
          {displayDoc && (
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
              }}
            >
              {/* Metadata */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                  sx={{ mb: 1 }}
                >
                  {displayDoc.creatorName && `By ${displayDoc.creatorName} • `}
                  {displayDoc.readingTimeMinutes} min read
                </Typography>

                {/* Tags */}
                {displayDoc.tags && displayDoc.tags.length > 0 && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ mb: 2 }}
                  >
                    {displayDoc.tags.map((tag, idx) => (
                      <Chip
                        key={idx}
                        label={tag}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                )}

                {displayDoc.description && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mb: 2 }}
                  >
                    {displayDoc.description}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Markdown Content */}
              <Box className="markdown-content">
                <ReactMarkdown
                  components={{
                    h1: ({ node, ...props }) => (
                      <Typography
                        variant="h4"
                        sx={{ mt: 3, mb: 1 }}
                        {...props}
                      />
                    ),
                    h2: ({ node, ...props }) => (
                      <Typography
                        variant="h5"
                        sx={{ mt: 2.5, mb: 1 }}
                        {...props}
                      />
                    ),
                    h3: ({ node, ...props }) => (
                      <Typography
                        variant="h6"
                        sx={{ mt: 2, mb: 0.5 }}
                        {...props}
                      />
                    ),
                    p: ({ node, ...props }) => (
                      <Typography
                        variant="body1"
                        sx={{ mb: 1 }}
                        {...props}
                      />
                    ),
                    li: ({ node, ...props }) => (
                      <Typography
                        variant="body1"
                        component="li"
                        sx={{ ml: 2, mb: 0.5 }}
                        {...props}
                      />
                    ),
                    code: ({ node, inline, ...props }) =>
                      inline ? (
                        <Box
                          component="code"
                          sx={{
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#1e1e1e"
                                : "#f5f5f5",
                            padding: "2px 4px",
                            borderRadius: "3px",
                            fontFamily: "monospace",
                            fontSize: "0.9em",
                          }}
                          {...props}
                        />
                      ) : (
                        <Box
                          component="pre"
                          sx={{
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#1e1e1e"
                                : "#f5f5f5",
                            padding: "12px",
                            borderRadius: "4px",
                            overflow: "auto",
                            mb: 1,
                          }}
                        >
                          <code {...props} />
                        </Box>
                      ),
                    blockquote: ({ node, ...props }) => (
                      <Box
                        sx={{
                          borderLeft: `4px solid ${theme.palette.primary.main}`,
                          paddingLeft: "12px",
                          marginLeft: 0,
                          marginBottom: "12px",
                          color: theme.palette.text.secondary,
                        }}
                        {...props}
                      />
                    ),
                  }}
                >
                  {displayDoc?.content && displayDoc.content.trim()
                    ? displayDoc.content
                    : "No content available"}
                </ReactMarkdown>
              </Box>

              {/* Related Docs */}
              {displayDoc.relatedDocs && displayDoc.relatedDocs.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Related Documentation
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                  >
                    {displayDoc.relatedDocs.map((relatedDoc, idx) => (
                      <Chip
                        key={idx}
                        label={relatedDoc.title}
                        onClick={() => {
                          const found = docs.find(
                            (d) => d.key === relatedDoc.key
                          );
                          if (found) {
                            onDocSelect(found);
                          }
                        }}
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MarkdownViewer;
